import React, { useState, useEffect } from "react";
import { Loader, Statistic, Table, Icon, Message } from "semantic-ui-react";
import { map, size } from "lodash";
import moment from "moment";
import { HeaderPage, PrintHeader, PrintTable } from "../../components/Admin";
import { useOrder, usePrintOrientation } from "../../hooks";
import { getBusinessToday, getBusinessDaysAgo } from "../../utils/date";
import {
  buildPrepTimesReport,
  formatMinutes,
  PREP_WARN_MINUTES,
  PREP_LATE_MINUTES,
  SLOW_PRODUCT_MIN_SAMPLES,
} from "../../utils/prepTimes";

// Tiempos de Cocina: desde que entra el pedido hasta que cocina toca "Listo"
// en su pantalla (ver utils/prepTimes.js). Ruta /admin/prep-times.


const pct = (value) => `${Math.round(value)}%`;

export function PrepTimesReport() {
  const [dateFrom, setDateFrom] = useState(() => getBusinessDaysAgo(7));
  const [dateTo, setDateTo] = useState(getBusinessToday);
  const { loading, orders, getOrdersInRange } = useOrder();
  usePrintOrientation("portrait");

  useEffect(() => {
    if (dateFrom && dateTo) getOrdersInRange(dateFrom, dateTo);
  }, [dateFrom, dateTo]);

  const report = buildPrepTimesReport(orders || []);
  const dateRangeLabel = `Del ${moment(dateFrom).format("DD/MM/YYYY")} al ${moment(
    dateTo
  ).format("DD/MM/YYYY")}`;

  return (
    <>
      <div className="screen-only">
        <HeaderPage
          title="Tiempos de Cocina"
          btnTitle={
            <>
              <Icon name="print" /> Imprimir
            </>
          }
          btnClick={() => window.print()}
        />
      </div>

      <div
        className="screen-only"
        style={{ display: "flex", gap: "15px", margin: "10px 0 20px" }}
      >
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Desde</label>
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Hasta</label>
          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      </div>

      <PrintTable>
        <PrintHeader title="Tiempos de Cocina" subtitle={dateRangeLabel} />

        {loading ? (
          <Loader active inline="centered">
            Cargando...
          </Loader>
        ) : report.overall.count === 0 ? (
          <div className="print-content">
            <p>
              No hay platos marcados "Listo" en la pantalla de Cocina en este
              rango de fechas. El tiempo se mide desde que entra el pedido hasta
              que cocina toca "Listo".
            </p>
          </div>
        ) : (
          <div className="print-content">
            <p style={{ color: "#666" }}>
              Desde que entra el pedido hasta que cocina toca "Listo" en su
              pantalla. A tiempo: menos de {PREP_WARN_MINUTES} min · Demorado:{" "}
              {PREP_LATE_MINUTES} min o más.
            </p>

            <Statistic.Group widths="four" size="small" style={{ margin: "20px 0 30px" }}>
              <Statistic>
                <Statistic.Value>{report.overall.count}</Statistic.Value>
                <Statistic.Label>Platos medidos</Statistic.Label>
              </Statistic>
              <Statistic color="blue">
                <Statistic.Value>{formatMinutes(report.overall.average)}</Statistic.Value>
                <Statistic.Label>Promedio</Statistic.Label>
              </Statistic>
              <Statistic color="green">
                <Statistic.Value>{pct(report.overall.onTime)}</Statistic.Value>
                <Statistic.Label>A tiempo</Statistic.Label>
              </Statistic>
              <Statistic color="red">
                <Statistic.Value>{pct(report.overall.late)}</Statistic.Value>
                <Statistic.Label>Demorados</Statistic.Label>
              </Statistic>
            </Statistic.Group>

            <h4>Por área</h4>
            <Table compact unstackable celled style={{ marginBottom: 30 }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Área</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Platos</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Promedio</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Mediana</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right" title="9 de cada 10 platos salen en menos de este tiempo">
                    9 de 10 en
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Más lento</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">A tiempo</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Demorados</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {map(report.areas, (row) => (
                  <Table.Row key={row.area}>
                    <Table.Cell>
                      <strong>{row.area}</strong>
                    </Table.Cell>
                    <Table.Cell textAlign="right">{row.count}</Table.Cell>
                    <Table.Cell textAlign="right">{formatMinutes(row.average)}</Table.Cell>
                    <Table.Cell textAlign="right">{formatMinutes(row.median)}</Table.Cell>
                    <Table.Cell textAlign="right">{formatMinutes(row.p90)}</Table.Cell>
                    <Table.Cell textAlign="right">{formatMinutes(row.max)}</Table.Cell>
                    <Table.Cell textAlign="right" positive={row.onTime >= 80}>
                      {pct(row.onTime)}
                    </Table.Cell>
                    <Table.Cell textAlign="right" negative={row.late > 10}>
                      {pct(row.late)}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>

            <h4>Platos más lentos</h4>
            {size(report.slowestProducts) === 0 ? (
              <p style={{ color: "#666" }}>
                Todavía no hay platos con al menos {SLOW_PRODUCT_MIN_SAMPLES}{" "}
                pedidos medidos en este rango.
              </p>
            ) : (
              <Table compact unstackable celled style={{ marginBottom: 30 }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Plato</Table.HeaderCell>
                    <Table.HeaderCell>Área</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Pedidos</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Promedio</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Más lento</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {map(report.slowestProducts, (row) => (
                    <Table.Row key={`${row.title}-${row.area}`}>
                      <Table.Cell>{row.title}</Table.Cell>
                      <Table.Cell>{row.area}</Table.Cell>
                      <Table.Cell textAlign="right">{row.count}</Table.Cell>
                      <Table.Cell textAlign="right">{formatMinutes(row.average)}</Table.Cell>
                      <Table.Cell textAlign="right">{formatMinutes(row.max)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}

            {report.skippedCount > 0 && (
              <Message info size="small">
                {report.skippedCount} plato(s) se entregaron sin pasar por la
                pantalla de Cocina (el mozo los marcó "Entregado" directo, ej.
                bebidas): no entran en estos tiempos.
              </Message>
            )}
          </div>
        )}
      </PrintTable>
    </>
  );
}
